import { Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { ChevronRightIcon, SearchIcon } from '~/components/icons'
import { PageDialog } from '~/components/page-dialog'
import { useDataState } from '~/data/data-context'
import type { LoggedSet } from '~/data/types'
import { formatDayLabel } from '~/lib/dates'
import { withoutDialog } from '~/lib/dialogs'
import { workoutMatches } from '~/lib/set-summary'

/**
 * Every workout, newest first, findable by anything that was in it. The timeline carries the
 * handful you are actually moving between; this is the rest of them.
 */
export function WorkoutsDialog({ onClose }: { onClose: () => void }) {
  const state = useDataState()
  const [query, setQuery] = useState('')

  /*
   * A workout is looked for by whatever you remember of it, which is rarely the name you gave
   * it — far more often it is something you did in it. So the search reads the sets too: the
   * exercises they were of, and anything written on them or on the workout itself.
   */
  const setsByWorkout = useMemo(() => {
    const byWorkout = new Map<string, LoggedSet[]>()
    for (const set of state.sets) {
      if (set.workoutId === null) {
        continue
      }
      const held = byWorkout.get(set.workoutId)
      if (held === undefined) {
        byWorkout.set(set.workoutId, [set])
      } else {
        held.push(set)
      }
    }
    return byWorkout
  }, [state.sets])

  const wanted = query.trim().toLowerCase()
  const shown = useMemo(
    () =>
      wanted === ''
        ? state.workouts
        : state.workouts.filter((workout) =>
            workoutMatches(workout, setsByWorkout.get(workout.id) ?? [], wanted),
          ),
    [state.workouts, setsByWorkout, wanted],
  )

  return (
    <PageDialog title="Workouts" onClose={onClose}>
      <div className="space-y-6">
        <label className="flex items-center gap-3 rounded-full bg-surface-raised px-4 py-2.5 focus-within:ring-2 focus-within:ring-accent">
          <SearchIcon className="h-6 w-6 shrink-0 text-ink-muted" />
          <span className="sr-only">Search workouts, exercises, notes</span>
          <input
            type="search"
            value={query}
            placeholder="Search workouts, exercises, notes"
            onChange={(event) => {
              setQuery(event.target.value)
            }}
            className="flex-1 bg-transparent text-ink outline-none placeholder:text-ink-muted"
          />
        </label>

        {shown.length === 0 ? (
          <p className="py-16 text-center text-ink-muted">
            {state.workouts.length === 0
              ? 'No workouts yet. Finish one and it lands here.'
              : 'No workout matches that.'}
          </p>
        ) : (
          <ul>
            {shown.map((workout) => {
              const count = workout.setCount ?? (setsByWorkout.get(workout.id) ?? []).length
              return (
                <li key={workout.id}>
                  <Link
                    to="/workouts/$workoutId"
                    params={{ workoutId: workout.id }}
                    search={withoutDialog}
                    className="group flex items-center gap-3 rounded-2xl px-2 py-3 transition-colors hover:bg-hover"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-ink">
                        {workout.name ?? 'Workout'}
                      </span>
                      <span className="block truncate text-sm text-ink-muted">
                        {formatDayLabel(workout.startedAt)} · {count} {count === 1 ? 'set' : 'sets'}
                      </span>
                    </span>
                    <ChevronRightIcon className="h-6 w-6 shrink-0 text-ink-muted transition-transform duration-150 group-hover:translate-x-1" />
                  </Link>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </PageDialog>
  )
}
